import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AppContext } from '../../App';
import { CosmosButton, CosmosTitle } from '../../cosmos/web-scoped/react';
import { Modal, ModalHeader, ModalBody } from './index';
import { ModalSelect } from './select';
import { getTrans } from '../../Utils/translate';
import athlete_skeleton from './no-image.png';
import athlete_skeleton_circle from './no-image-circle.png';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { GAtrack } from '../../Utils/tracking';
import { ProfileMeta } from './comparison/ProfileMeta';
import { ProfileStats } from './comparison/ProfileStats';
import { dataFetcher } from '../../Utils/dataFetcher';
import './comparison.scss';

const ModalComparison = ({
  className,
  config,
  athleteLeftId,
  athleteRightId,
  list,
  layout = 'normal',
  onClick
}) => {
  const athleteDataFallback = {
    age: undefined,
    athlete_id: undefined,
    nationality: undefined,
    first_name: undefined,
    last_name: undefined,
    image_profile: undefined,
    value: undefined,
    career_stats: undefined
  };
  const [athleteLeft, setAthleteLeft] = useState(athleteDataFallback);
  const [athleteRight, setAthleteRight] = useState(athleteDataFallback);
  const [visible, setVisible] = useState(false);
  const [headerBounds, setHeaderBounds] = useState({ width: 0, height: 0 });

  const headerRef = useRef(null);
  const context = React.useContext(AppContext);
  const { compareAthlete, comparison, selectText } = context.lang;

  const fetchAthleteById = useCallback(async (athleteId, uri) => {
    const needle = uri.indexOf('{athleteId}') !== -1 ? '{athleteId}' : '{id}';
    const data = await dataFetcher(
      uri.replace(needle, athleteId),
      context.config.uriConfig
    );
    if (data && data.athlete) {
      const { age, first_name, last_name, id, image, nationality } = data.athlete;
      const { career_stats, series_rankings } = data;
      return {
        age: age,
        athlete_id: id,
        nationality: nationality.iso3,
        first_name: first_name,
        last_name: last_name,
        image_profile: image.profile,
        value: `iwdd-${id}`,
        career_stats: career_stats,
        series_rankings: series_rankings
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (athleteLeftId) {
      fetchAthleteById(athleteLeftId, getAthleteDetailUri()).then((data) => {
        setAthleteLeft(data);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, fetchAthleteById, athleteLeftId]);

  useEffect(() => {
    if (athleteRightId) {
      fetchAthleteById(athleteRightId, getAthleteDetailUri()).then((data) => {
        setAthleteRight(data);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, fetchAthleteById, athleteRightId]);

  useEffect(() => {
    if (visible) {
      const navBoundsHeight = context.navBounds ? context.navBounds.height : 80;
      // init UI
      setHeaderBounds({
        width: headerRef && headerRef.current ? headerRef.current.clientWidth : 0,
        // +context.navBounds.height for the navigation tabs
        height:
          headerRef && headerRef.current
            ? headerRef.current.clientHeight + navBoundsHeight
            : 0
      });
    } else {
      // reset UI on modal close
      setAthleteRight(athleteDataFallback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const getAthleteDetailUri = () => {
    const candidates = ['uriAthlete', 'uriDetail'];
    const key = candidates.find((key) => {
      return config.hasOwnProperty(key);
    });
    return key ? config[key] : undefined;
  };

  const onChangeAthlete = (value, side) => {
    const athleteId = value.athlete_id
      ? value.athlete_id
      : value.value
      ? value.value
      : value;
    fetchAthleteById(athleteId, getAthleteDetailUri()).then((newAthlete) => {
      if (side === 'left') {
        setAthleteLeft(newAthlete);
      } else {
        setAthleteRight(newAthlete);
      }
      const dataLayerArgs = {
        // dataLayer: {
        event: 'rbVideoTracking',
        event_name: 'video_sidebar',
        video_sidebar: {
          value: `${config.type} Comparison - ${newAthlete.first_name} ${newAthlete.last_name}`,
          widget_type: `${config.type} - ${
            config.trackingTitle || config.navigationTitle
          }`,
          widget_type_profile: `${newAthlete.first_name} ${newAthlete.last_name}`
        }
        // eventCategory: 'video',
        // eventAction: 'sidebar'
        // }
      };
      GAtrack(dataLayerArgs);
    });
  };

  const onChangeAthleteLeft = (value) => {
    onChangeAthlete(value, 'left');
  };

  const onChangeAthleteRight = (value) => {
    onChangeAthlete(value, 'right');
  };

  return (
    <>
      <div
        className={`modalComparison layout-${layout} ${visible ? ' open' : ''}${
          className ? ` ${className}` : ''
        }`}
      >
        <div className={`current`}>
          <CosmosButton
            onClick={() => {
              setVisible(true);
              onClick();
            }}
            class="modal-comparison__button"
            kind="secondary"
            shape="pill"
          >
            {getTrans(compareAthlete)}
          </CosmosButton>
        </div>

        <Modal
          className={`modalComparison modal-seamless layout-${layout}`}
          layout="interim"
          show={visible}
          setShow={setVisible}
        >
          <ModalHeader ref={headerRef}>
            <div className="modal__header-title">
              <CosmosTitle>{getTrans(comparison)}</CosmosTitle>
            </div>
          </ModalHeader>
          <ModalBody>
            <PerfectScrollbar
              style={{ maxHeight: `calc(100vh - ${headerBounds.height}px)` }}
              options={{ wheelPropagation: true }}
            >
              <div className="athletes-meta athletes-meta-images">
                <div className={`selector ${athleteLeft ? '' : 'fallback'}`}>
                  <ModalSelect
                    headerText={
                      selectText ? getTrans(selectText).toUpperCase() : 'CHANGE ATHLETE'
                    }
                    options={list}
                    value={{ value: athleteLeft.value }}
                    disabledOptions={[athleteRight.value]}
                    onChange={onChangeAthleteLeft}
                    comparison={comparison}
                    showSelectAthleteImage={config.showSelectAthleteImage}
                  >
                    <img
                      className="profile-image"
                      src={
                        athleteLeft.image_profile ||
                        (layout === 'normal' ? athlete_skeleton : athlete_skeleton_circle)
                      }
                      alt={`img-${athleteLeft.last_name || 'athlete'}`}
                    />
                    <CosmosButton
                      class="button-label"
                      decorative
                      kind="secondary"
                      shape="pill"
                      size="small"
                      width="content"
                    >
                      {getTrans(selectText)}
                    </CosmosButton>
                  </ModalSelect>
                </div>

                <div className={`selector ${athleteRight ? '' : 'fallback'}`}>
                  <ModalSelect
                    headerText={
                      selectText ? getTrans(selectText).toUpperCase() : 'CHANGE ATHLETE'
                    }
                    options={list}
                    value={{ value: athleteRight.value }}
                    disabledOptions={[athleteLeft.value]}
                    onChange={onChangeAthleteRight}
                    comparison={comparison}
                    showSelectAthleteImage={config.showSelectAthleteImage}
                  >
                    <img
                      className="profile-image"
                      src={
                        athleteRight.image_profile ||
                        (layout === 'normal' ? athlete_skeleton : athlete_skeleton_circle)
                      }
                      alt={`img-${athleteRight.last_name || 'athlete'}`}
                    />
                    <CosmosButton
                      class="button-label"
                      decorative
                      kind="secondary"
                      shape="pill"
                      size="small"
                      width="content"
                    >
                      {getTrans(selectText)}
                    </CosmosButton>
                  </ModalSelect>
                </div>
              </div>

              <div className="athletes-meta athletes-meta-profiles">
                <ProfileMeta athlete={athleteLeft} />
                <ProfileMeta athlete={athleteRight} />
              </div>

              <div className="athletes-comparison">
                <ProfileStats
                  athleteLeft={athleteLeft}
                  athleteRight={athleteRight}
                  eventType={config.eventType}
                />
              </div>
            </PerfectScrollbar>
          </ModalBody>
        </Modal>
      </div>
    </>
  );
};

export { ModalComparison };
